/**
 * AttachmentGallery - Display photos from Epicor in a visual gallery
 * Renders images grouped by order line with lightbox for full-size viewing
 * Supports selecting photos for email
 *
 * NOW SUPPORTS TWO MODES:
 * 1. URL-based (type: "photo_gallery_urls") - Images loaded via URLs, no token overhead
 * 2. Base64 (type: "photo_gallery") - Legacy mode with embedded image data
 */

import { useState } from 'react'
import { X, Mail, Check, Download, ZoomIn, ChevronLeft, ChevronRight } from 'lucide-react'

/**
 * Parse gallery data from tool result
 * Returns null if not a gallery, otherwise returns structured gallery data
 */
export function parseGalleryData(content) {
  // Check if content contains photo gallery JSON
  if (!content || typeof content !== 'string') return null

  // Look for JSON with type: "photo_gallery" or "photo_gallery_urls"
  try {
    // Try to find JSON in the content
    const jsonMatch = content.match(/\{[\s\S]*"type"\s*:\s*"photo_gallery[^"]*"[\s\S]*\}/)
    if (jsonMatch) {
      const data = JSON.parse(jsonMatch[0])
      if ((data.type === 'photo_gallery' || data.type === 'photo_gallery_urls') && data.for_display) {
        return data
      }
    }
  } catch (e) {
    // Not valid JSON, that's fine
  }

  return null
}

/**
 * Get the image source - handles both URL and base64 modes
 */
function getImageSrc(photo, thumbnail = true) {
  // URL-based mode (new, preferred)
  if (photo.thumbnail_url || photo.full_url) {
    return thumbnail ? photo.thumbnail_url : photo.full_url
  }

  // Base64 mode (legacy)
  if (photo.content_base64) {
    return `data:${photo.media_type || 'image/jpeg'};base64,${photo.content_base64}`
  }

  return null
}

/**
 * Check if photo has viewable content
 */
function hasContent(photo) {
  return (photo.thumbnail_url || photo.full_url || photo.content_base64) && !photo.download_error
}

/**
 * Individual photo thumbnail with click to expand
 */
function PhotoThumbnail({ photo, onClick, isSelected, onSelect }) {
  const canView = hasContent(photo)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const imageSrc = getImageSrc(photo, true)

  return (
    <div className="relative group">
      {/* Selection checkbox */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          onSelect(photo)
        }}
        className={`absolute top-2 left-2 z-10 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
          isSelected
            ? 'bg-amtrend-blue border-amtrend-blue text-white'
            : 'bg-white/80 dark:bg-gray-800/80 border-gray-300 dark:border-gray-600 text-transparent hover:border-amtrend-blue'
        }`}
      >
        <Check size={14} />
      </button>

      {/* Photo */}
      <div
        onClick={() => canView && onClick(photo)}
        className={`relative overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer transition-all hover:shadow-md hover:border-amtrend-blue/50 ${
          !canView ? 'opacity-50 cursor-not-allowed' : ''
        }`}
      >
        {canView && imageSrc ? (
          <>
            {/* Loading placeholder */}
            {loading && !error && (
              <div className="absolute inset-0 bg-gray-100 dark:bg-surface-dark-overlay animate-pulse flex items-center justify-center">
                <span className="text-gray-400 text-sm">Loading...</span>
              </div>
            )}

            <img
              src={imageSrc}
              alt={photo.filename || 'Photo'}
              className={`w-full h-32 object-cover ${loading && !error ? 'opacity-0' : 'opacity-100'}`}
              onLoad={() => setLoading(false)}
              onError={() => {
                setLoading(false)
                setError(true)
              }}
            />

            {error && (
              <div className="absolute inset-0 bg-gray-100 dark:bg-surface-dark-overlay flex items-center justify-center">
                <span className="text-gray-400 text-sm">Load failed</span>
              </div>
            )}

            {/* Zoom overlay on hover */}
            {!error && (
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <ZoomIn size={24} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            )}
          </>
        ) : (
          <div className="w-full h-32 bg-gray-100 dark:bg-surface-dark-overlay flex items-center justify-center">
            <span className="text-gray-400 text-sm">Failed to load</span>
          </div>
        )}

        {/* Filename label */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
          <p className="text-white text-xs truncate">
            {photo.filename || 'Photo'}
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * Lightbox for full-size photo viewing
 */
function Lightbox({ photo, photos, onClose, onNext, onPrev, hasNext, hasPrev }) {
  const [loading, setLoading] = useState(true)

  if (!photo) return null

  const fullSrc = getImageSrc(photo, false)

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
      >
        <X size={24} />
      </button>

      {/* Navigation arrows */}
      {hasPrev && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onPrev()
          }}
          className="absolute left-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
        >
          <ChevronLeft size={32} />
        </button>
      )}

      {hasNext && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onNext()
          }}
          className="absolute right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
        >
          <ChevronRight size={32} />
        </button>
      )}

      {/* Image */}
      <div onClick={(e) => e.stopPropagation()} className="max-w-[90vw] max-h-[85vh]">
        {loading && (
          <div className="w-96 h-64 bg-gray-800 animate-pulse rounded-lg flex items-center justify-center">
            <span className="text-gray-400">Loading full image...</span>
          </div>
        )}

        <img
          src={fullSrc}
          alt={photo.filename}
          className={`max-w-full max-h-[85vh] object-contain rounded-lg ${loading ? 'hidden' : 'block'}`}
          onLoad={() => setLoading(false)}
        />

        {/* Caption */}
        <div className="mt-3 text-center">
          <p className="text-white font-medium">{photo.filename}</p>
          {photo.description && (
            <p className="text-gray-300 text-sm mt-1">{photo.description}</p>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Main Gallery Component
 */
export default function AttachmentGallery({ galleryData, onEmailRequest }) {
  const [selectedPhotos, setSelectedPhotos] = useState([])
  const [lightboxPhoto, setLightboxPhoto] = useState(null)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  // Flatten all photos for lightbox navigation
  const allPhotos = galleryData.lines?.flatMap(line =>
    line.photos.filter(p => hasContent(p))
  ) || []

  const toggleSelect = (photo) => {
    setSelectedPhotos(prev => {
      const isSelected = prev.some(p => p.xfile_ref_num === photo.xfile_ref_num)
      if (isSelected) {
        return prev.filter(p => p.xfile_ref_num !== photo.xfile_ref_num)
      } else {
        return [...prev, photo]
      }
    })
  }

  const openLightbox = (photo) => {
    const index = allPhotos.findIndex(p => p.xfile_ref_num === photo.xfile_ref_num)
    setLightboxIndex(index >= 0 ? index : 0)
    setLightboxPhoto(photo)
  }

  const closeLightbox = () => {
    setLightboxPhoto(null)
  }

  const nextPhoto = () => {
    const nextIndex = lightboxIndex + 1
    if (nextIndex < allPhotos.length) {
      setLightboxIndex(nextIndex)
      setLightboxPhoto(allPhotos[nextIndex])
    }
  }

  const prevPhoto = () => {
    const prevIndex = lightboxIndex - 1
    if (prevIndex >= 0) {
      setLightboxIndex(prevIndex)
      setLightboxPhoto(allPhotos[prevIndex])
    }
  }

  const handleEmailSelected = () => {
    if (selectedPhotos.length === 0) return
    if (onEmailRequest) {
      // Generate a message asking to email these photos with xfile_refs for the tool
      const photoList = selectedPhotos.map(p => p.filename).join(', ')
      const xfileRefs = selectedPhotos.map(p => p.xfile_ref_num).join(', ')
      onEmailRequest(`Email these ${selectedPhotos.length} photos from order ${galleryData.order_num}: ${photoList} (xfile_refs: ${xfileRefs})`)
    }
  }

  if (!galleryData.lines || galleryData.lines.length === 0) {
    return (
      <div className="bg-gray-50 dark:bg-surface-dark-raised rounded-lg p-4 text-center text-gray-500 dark:text-gray-400">
        <p>{galleryData.message || 'No photos found'}</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header with email action */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Photos for Order {galleryData.order_num}
          <span className="ml-2 text-gray-400">({galleryData.total_photos} photos)</span>
        </h3>

        {selectedPhotos.length > 0 && (
          <button
            onClick={handleEmailSelected}
            className="flex items-center gap-2 px-3 py-1.5 bg-amtrend-blue text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Mail size={16} />
            Email {selectedPhotos.length} selected
          </button>
        )}
      </div>

      {/* Gallery grouped by order line */}
      {galleryData.lines.map((line) => (
        <div key={line.order_line} className="bg-white dark:bg-surface-dark-raised rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="mb-3">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Line {line.order_line}
              {line.part_num && (
                <span className="ml-2 font-normal text-gray-500 dark:text-gray-400">
                  {line.part_num}
                </span>
              )}
              <span className="ml-2 text-gray-400 font-normal">({line.count} photos)</span>
            </h4>
            {(line.xpart_num || line.line_desc) && (
              <p className="text-xs text-gray-400 mt-0.5">
                {line.xpart_num && <span className="mr-3">Customer P/N: {line.xpart_num}</span>}
                {line.line_desc && <span className="truncate">{line.line_desc}</span>}
              </p>
            )}
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
            {line.photos.map((photo) => (
              <PhotoThumbnail
                key={photo.xfile_ref_num}
                photo={photo}
                onClick={openLightbox}
                isSelected={selectedPhotos.some(p => p.xfile_ref_num === photo.xfile_ref_num)}
                onSelect={toggleSelect}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Selection hint */}
      {selectedPhotos.length === 0 && (
        <p className="text-xs text-gray-400 text-center">
          Click the circle on photos to select them for email
        </p>
      )}

      {/* Lightbox */}
      <Lightbox
        photo={lightboxPhoto}
        photos={allPhotos}
        onClose={closeLightbox}
        onNext={nextPhoto}
        onPrev={prevPhoto}
        hasNext={lightboxIndex < allPhotos.length - 1}
        hasPrev={lightboxIndex > 0}
      />
    </div>
  )
}
